import { buildSearchQuery } from '@/utils/search/searchQuery';
import { changePaginationPageSize, getSearchPageSize } from '@/redux/modules/pagination';
import { Component } from 'react';
import {
    createSaveSearchKeyBySearchParams,
    getIsSavedSearch,
    getSaveSearchParams,
    ParamsOrKey,
    submitSaveSearch,
} from '@/redux/modules/saveSearch';
import { difference } from 'es-toolkit';
import {
    fetchSearchSuggestions,
    getSearchSuggestions,
    getSecondarySearchSuggestions,
    SearchSuggestion,
} from '@/redux/modules/searchSuggestions';
import { FilterAction } from '@/types/SearchFacet';
import { getAuthToken, isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { getBaseUrl } from '@/redux/modules/config';
import { getFetchFacetsSubmitted } from '@/redux/modules/searchFacets';
import { getFilterActionByFacetId } from './utils/getFilterActionByFacetId';
import { getIsWhiteLabel, getWhiteLabelSellerId } from '@/redux/modules/whiteLabel';
import { getOpenModal } from '@/redux/modules/modal.selectors';
import {
    getPageFromQueryParams,
    modifyParamsRemoveAllExceptCategories,
    modifyParamsRemoveCountryAndDistance,
    modifyParamsRemoveDomesticFlatShipping,
    modifyParamsRemoveFacet,
    modifyParamsRemovePage,
    modifyParamsRemoveParamByFacetId,
    modifyParamsRemovePublishDate,
    modifyParamsRemoveSaleDates,
    modifyParamsRemoveSaveSearch,
    modifyParamsReplaceCountry,
    modifyParamsWhenChecked,
    modifyParamsWithDistance,
    modifyParamsWithDomesticFlatShipping,
    modifyParamsWithFilterTerm,
    modifyParamsWithPage,
    modifyParamsWithPageSize,
    modifyParamsWithRange,
    modifyParamsWithSort,
    modifyParamsWithTaxonomyAll,
    modifyParamsWithTaxonomyParent,
    parseQueryParams,
    removeQueryParams,
    stringifyQuery,
} from '@/utils/queryParams';
import { getSearchInputBuffer, getSellersFromSearchedItems } from '@/redux/modules/search/search.selectors';
import { getSearchUrlWithParams, getSimilarItemsPageUrlWithParams, isSimilarItemsPage } from '@/utils/urls';
import { getUserExcludedHouses } from '@/redux/modules/search/exclusions/searchExclusions.selectors';
import { IntlShape, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { openLoginModal } from '@/redux/modules/modal';
import { SearchPageName, SearchQueryStatus } from '@/types/search/searchQuery';
import { SearchParams as SearchParamsType } from '@/types/SaveSearch';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';
import { setHeaderDropdownVisible } from '@/redux/modules/headerSearchDropdown';
import { submitSearch } from '@/redux/modules/search/search.actions';
import { toast } from '@liveauctioneers/hammer-ui-core/toast';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOptimizedSearchSelectors } from '@/hooks/useOptimizedSearchSelectors';
import cleanSearchTerms from '@/utils/cleanSearchTerms';
import FacetTypes from '@/types/search/enums/facetTypes';
import isEqual from 'react-fast-compare';
import paramTypes from '@/types/search/enums/paramTypes';
import SearchPage from './SearchPage';

type SearchPageContainerProps = {
    archivedSearch: boolean;
    authToken: string;
    baseUrl: string;
    changePaginationPageSize: (pageSize: number | 'all') => void;
    excludedFacets: string[];
    facets: any[];
    fetchSearchSuggestions: (term: string, secondary?: boolean) => void;
    filterTerms: string[];
    formatMessage: IntlShape['formatMessage'];
    houses: any[];
    isLoading: boolean;
    isLoggedIn: boolean;
    isSavedSearch: boolean;
    loginModalOpen: boolean;
    navigate: ReturnType<typeof useNavigate>;
    openLoginModal: () => void;
    page: number;
    pageName: string;
    pageSize: number;
    pathname: string;
    queryParams: any;
    rawFacets: any[];
    saveSearchParams: SearchParamsType;
    searchFailed: boolean;
    searchQuery: any;
    searchSubmitted: boolean;
    sellerId: number;
    sellersInSearchResults: number[];
    setHeaderDropdownVisible: (show?: boolean) => void;
    sortOptions: SearchSortAndDirection[];
    submitSaveSearch: (ssParams: ParamsOrKey, sendAlert: boolean) => void;
    submitSearch: (params: Parameters<typeof submitSearch>[0]) => void;
    suggestions: SearchSuggestion[];
    totalFound: number;
    totalLiveItems: number;
    totalPages: number;
    totalSoldItems: number;
    userExcludedHouses: number[];
    whiteLabel: boolean;
};

/**
 * SearchPageContainer
 * serves content for /search/ and /similar-items/ endpoints
 */
class SearchPageContainer extends Component<SearchPageContainerProps> {
    componentDidMount() {
        const { fetchSearchSuggestions, searchQuery } = this.props;
        fetchSearchSuggestions(cleanSearchTerms(searchQuery.searchTerm));
    }

    componentDidUpdate(prevProps) {
        // Perform search if excluded facets change
        if (!isEqual(prevProps.userExcludedHouses, this.props.userExcludedHouses)) {
            const { searchQuery } = this.props;
            const pageName = searchQuery.options.status.includes('sold')
                ? SearchPageName.SOLD_SEARCH
                : SearchPageName.SEARCH;
            this.props.submitSearch({ pageName, searchQuery });
        }

        // If the url has the saveSearch param, save the search and redirect
        if (
            !prevProps.authToken &&
            this.props.authToken &&
            this.props.queryParams.saveSearch &&
            !this.props.isSavedSearch
        ) {
            this.submitSaveSearch();
            let newParams = modifyParamsRemoveSaveSearch(this.props.queryParams);
            this.navigateWithParams(newParams);

            toast('success', {
                description: this.props.formatMessage({
                    id: 'toastMessages.descriptions.searchSaved',
                }),
                title: this.props.formatMessage({
                    id: 'toastMessages.titles.success',
                }),
            });
        }

        // If the url has the saveSearch param, but we are not logged in, open the log in modal
        if (
            !this.props.loginModalOpen &&
            !prevProps.authToken &&
            !this.props.authToken &&
            this.props.queryParams.saveSearch
        ) {
            this.props.openLoginModal();
        }

        // Because component did update gets call multiple times after a navigate call is made, we cant rely completly
        // on the "page" property to decide whether to call the navigate or not.  Because of this we have a boolean
        // that we set on page load that will only be true the first time and then once set wont be true again.
        // this will allow us to redirect properly to the first page in cases where a bidder has a page in the query
        // that doesn't contain results
        if (
            !this.props.archivedSearch &&
            this.props.page > 1 &&
            Math.ceil(this.props.totalLiveItems / this.props.pageSize) < this.props.page &&
            !this.navigating
        ) {
            const newParams = modifyParamsWithPage(this.props.queryParams, 1);
            this.navigateWithParams(newParams);
            this.navigating = true;
        }

        if (
            this.props.archivedSearch &&
            this.props.page > 1 &&
            Math.ceil(this.props.totalSoldItems / this.props.pageSize) < this.props.page &&
            !this.navigating
        ) {
            const newParams = modifyParamsWithPage(this.props.queryParams, 1);
            this.navigateWithParams(newParams);
            this.navigating = true;
        }
    }

    componentWillUnmount() {
        this.props.setHeaderDropdownVisible(false);
    }

    navigating: boolean;

    changePageAndNavigate = (selected) => {
        const page = selected + 1;
        const newParams = modifyParamsWithPage(this.props.queryParams, page);
        this.navigateWithParams(newParams, FilterAction.PageChanged);
    };

    changePageSizeAndNavigate = (pageSize) => {
        this.props.changePaginationPageSize(pageSize);
        const newParams = modifyParamsWithPageSize(this.props.queryParams, pageSize);
        this.navigateWithParams(newParams, FilterAction.PageSizeChanged);
    };

    clearAllParamsExceptCategories = () => {
        let newParams = modifyParamsRemoveAllExceptCategories(this.props.queryParams, this.props.facets);
        newParams = modifyParamsRemovePage(newParams);
        this.navigateWithParams(newParams, FilterAction.CategoryChanged);
    };

    distanceSelected = (country, zipCode, range, measurement) => {
        let newParams = modifyParamsWithDistance(this.props.queryParams, country, zipCode, range, measurement);
        newParams = modifyParamsRemovePage(newParams);
        this.navigateWithParams(newParams, FilterAction.LocationChanged);
    };

    domesticFlatShippingChecked = (value, checked) => {
        let newParams = this.props.queryParams;
        if (value === 'free' && checked === true) {
            newParams = modifyParamsWithDomesticFlatShipping(this.props.queryParams, 0);
        } else if (value === 'flat' && checked === true) {
            newParams = modifyParamsWithDomesticFlatShipping(this.props.queryParams, true);
        } else if (checked === false) {
            newParams = modifyParamsRemoveDomesticFlatShipping(this.props.queryParams);
        }
        this.navigateWithParams(newParams, checked ? FilterAction.ShippingChanged : FilterAction.ShippingRemoved);
    };

    facetOptionChecked = (facetId: string, value: any, checked: boolean) => {
        let newParams = modifyParamsWhenChecked(this.props.queryParams, facetId, value, checked);
        newParams = modifyParamsRemovePage(newParams);
        const filterAction = getFilterActionByFacetId({ facetId, isRemovingFilter: !checked });
        this.navigateWithParams(newParams, filterAction);
    };

    navigateWithParams = (params, filterAction?: FilterAction) => {
        let newParams = params;
        if (params.page === 1) {
            newParams = modifyParamsRemovePage(newParams);
        }
        if (isSimilarItemsPage(this.props.pathname)) {
            this.props.navigate(getSimilarItemsPageUrlWithParams(this.props.pathname, params));
        } else {
            this.props.navigate(getSearchUrlWithParams(newParams), { state: { filterAction } });
        }
    };

    rangeEntered = (facetId: string, min: string, max: string) => {
        let newParams = modifyParamsWithRange(this.props.queryParams, facetId, Number(min), Number(max));

        if (min === '' && max === '') {
            newParams = modifyParamsRemoveParamByFacetId(newParams, facetId);
        }
        const filterAction = getFilterActionByFacetId({ facetId, isRemovingFilter: min === '' && max === '' });
        newParams = modifyParamsRemovePage(newParams);

        this.navigateWithParams(newParams, filterAction);
    };

    removeCountryAndDistanceSelected = () => {
        let newParams = modifyParamsRemoveCountryAndDistance(this.props.queryParams);
        newParams = modifyParamsRemovePage(newParams);
        const filterAction = getFilterActionByFacetId({
            facetId: FacetTypes.COUNTRY_CODE_ID,
            isRemovingFilter: true,
        });
        this.navigateWithParams(newParams, filterAction);
    };

    replaceCountrySelected = (countryId) => {
        let newParams = modifyParamsReplaceCountry(this.props.queryParams, countryId);
        newParams = modifyParamsRemovePage(newParams);
        const filterAction = getFilterActionByFacetId({
            facetId: FacetTypes.COUNTRY_CODE_ID,
            isRemovingFilter: false,
        });
        this.navigateWithParams(newParams, filterAction);
    };

    removeFacetById = (facetId) => {
        let newParams = modifyParamsRemoveFacet(this.props.queryParams, facetId);
        newParams = modifyParamsRemovePage(newParams);
        const filterAction = getFilterActionByFacetId({
            facetId,
            isRemovingFilter: true,
        });
        this.navigateWithParams(newParams, filterAction);
    };

    removePublishedDate = () => {
        let newParams = modifyParamsRemovePublishDate(this.props.queryParams);
        newParams = modifyParamsRemovePage(newParams);

        this.navigateWithParams(newParams, FilterAction.PublishedDateRemoved);
    };

    removeSaleDates = () => {
        let newParams = modifyParamsRemoveSaleDates(this.props.queryParams);
        newParams = modifyParamsRemovePage(newParams);
        this.navigateWithParams(newParams, FilterAction.SaleDateRemoved);
    };

    removeQueryParams = (keys) => {
        const query = removeQueryParams(this.props.queryParams, keys);
        this.props.navigate({
            search: stringifyQuery(query),
        });
    };

    sortSelected = (sortOption) => {
        let newParams = modifyParamsWithSort(this.props.queryParams, sortOption);
        newParams = modifyParamsRemovePage(newParams);

        this.navigateWithParams(newParams, FilterAction.SortByChanged);
    };

    submitSaveSearch = () => {
        const ssParams = getSaveSearchParams(this.props.queryParams);
        const sendSearchAlert = true;
        this.props.submitSaveSearch(ssParams, sendSearchAlert);
    };

    taxonomyAllSelected = (facetId, categoryId) => {
        let newParams = modifyParamsWithTaxonomyAll(this.props.queryParams, facetId, categoryId);
        newParams = modifyParamsRemovePage(newParams);
        const filterAction = getFilterActionByFacetId({
            facetId,
            isRemovingFilter: newParams?.category_id?.includes(categoryId) ? false : true,
        });
        this.navigateWithParams(newParams, filterAction);
    };

    taxonomyParentSelected = (parentId, categoryId, facetId) => {
        let newParams = modifyParamsWithTaxonomyParent(this.props.queryParams, facetId, categoryId, parentId);
        newParams = modifyParamsRemovePage(newParams);
        const filterAction = getFilterActionByFacetId({
            facetId,
            isRemovingFilter: newParams?.category_id?.includes(categoryId) ? false : true,
        });
        this.navigateWithParams(newParams, filterAction);
    };

    toggleFilterTerm = (filterTerm: string) => {
        let newParams = modifyParamsWithFilterTerm(this.props.queryParams, filterTerm);
        newParams = modifyParamsRemovePage(newParams);
        const filterAction = newParams?.filterTerm?.includes(filterTerm)
            ? FilterAction.FilterTermAdded
            : FilterAction.FilterTermRemoved;
        this.navigateWithParams(newParams, filterAction);
    };

    render() {
        const {
            archivedSearch,
            baseUrl,
            excludedFacets,
            facets,
            filterTerms,
            houses,
            isLoading,
            isLoggedIn,
            page,
            pageName,
            pageSize,
            pathname,
            queryParams,
            saveSearchParams,
            searchFailed,
            searchQuery,
            searchSubmitted,
            sortOptions,
            totalFound,
            totalLiveItems,
            totalPages,
            totalSoldItems,
            whiteLabel,
        } = this.props;

        return (
            <SearchPage
                archivedSearch={archivedSearch}
                baseUrl={baseUrl}
                changePageAndNavigate={this.changePageAndNavigate}
                changePageSizeAndNavigate={this.changePageSizeAndNavigate}
                clearAllParamsExceptCategories={this.clearAllParamsExceptCategories}
                distanceSelected={this.distanceSelected}
                excludedFacets={excludedFacets}
                facetOptionChecked={this.facetOptionChecked}
                facets={facets}
                filterTerms={filterTerms}
                houses={houses}
                isLoading={isLoading}
                liveItemsFound={totalLiveItems}
                page={page}
                pageName={pageName}
                pageSize={pageSize}
                pathname={pathname}
                queryParams={queryParams}
                rangeEntered={this.rangeEntered}
                removeCountryAndDistance={this.removeCountryAndDistanceSelected}
                removeFacetById={this.removeFacetById}
                removePublishedDate={this.removePublishedDate}
                removeQueryParams={this.removeQueryParams}
                removeSaleDates={this.removeSaleDates}
                replaceCountry={this.replaceCountrySelected}
                saveSearchParams={saveSearchParams}
                searchFailed={searchFailed}
                searchQuery={searchQuery}
                searchSubmitted={searchSubmitted}
                soldItemsFound={totalSoldItems}
                sortOptions={sortOptions}
                sortSelected={this.sortSelected}
                taxonomyAllSelected={this.taxonomyAllSelected}
                taxonomyParentSelected={this.taxonomyParentSelected}
                toggleFilterTerm={this.toggleFilterTerm}
                totalItemsFound={totalFound}
                totalPages={totalPages}
                useSimilarItemsCard={!isLoggedIn && archivedSearch}
                whiteLabel={whiteLabel}
            />
        );
    }
}

/**
 * This function allowed removing react-redux connect and redux-first-history from the class above without rewriting its componentDidUpdate craziness
 */
const SearchPageContainerWrapper = () => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const { formatMessage } = useIntl();

    const queryParams = parseQueryParams(location.search);
    const filterTermQuery = queryParams[paramTypes.FILTER_TERM] || null;
    const filterTerms = filterTermQuery ? filterTermQuery.split(',') : [];

    // Use optimized selectors to batch Redux calls and reduce re-renders
    const {
        archivedSearch,
        currentFacets: facets,
        rawFacets,
        searchState,
    } = useOptimizedSearchSelectors(queryParams, location.pathname);

    const baseUrl = useAppSelector(getBaseUrl);
    const authToken = useAppSelector(getAuthToken);
    const userExcludedHouses = useAppSelector(getUserExcludedHouses);
    const sellersInSearchResults = useAppSelector(getSellersFromSearchedItems);
    const page = getPageFromQueryParams(queryParams);
    const pageSize = useAppSelector(getSearchPageSize);
    const saveSearchKey = createSaveSearchKeyBySearchParams(queryParams);
    const isSavedSearch = useAppSelector((state) => getIsSavedSearch(state, saveSearchKey));
    const saveSearchParams = getSaveSearchParams(queryParams);
    const searchQuery = buildSearchQuery({
        pathname: location.pathname,
        queryParams,
        rawFacets,
        searchPageSize: pageSize,
        similarItems: true,
    });
    const isLoggedIn = useAppSelector(isUserLoggedIn);

    const {
        error: searchFailed,
        isLoading,
        sort,
        submitted,
        totalFound,
        totalLiveItems,
        totalPages,
        totalSoldItems,
    } = searchState;

    const pageName = searchQuery.options.status.includes(SearchQueryStatus.Sold)
        ? SearchPageName.SOLD_SEARCH
        : SearchPageName.SEARCH;

    const fetchFacetsIsSubmitted = useAppSelector(getFetchFacetsSubmitted);
    const searchSubmitted = submitted || fetchFacetsIsSubmitted;

    let sortOptions = sort;
    // @ts-expect-error
    if (pageName !== 'catalog' && sortOptions.includes(SearchSortAndDirection.LotNumberLowest)) {
        sortOptions = difference(sortOptions, [SearchSortAndDirection.LotNumberLowest]);
    }

    const whiteLabel = useAppSelector(getIsWhiteLabel);

    const suggestionsObj = useAppSelector(getSearchSuggestions);
    const searchSuggestions = suggestionsObj[0];
    let { suggestions } = searchSuggestions;
    // remove searchTerm from suggestions

    const searchInput = useAppSelector(getSearchInputBuffer);
    suggestions = suggestions.filter((suggestion) => {
        return suggestion.value !== searchInput;
    });

    const { houses } = useAppSelector(getSecondarySearchSuggestions);
    const openModal = useAppSelector(getOpenModal);
    const loginModalOpen = openModal === 'LOGIN';

    const sellerId = useAppSelector(getWhiteLabelSellerId);

    const excludedFacets = [FacetTypes.KEYWORD_ID];
    // the location facet is unsupported for ended items so its excluded here
    if (archivedSearch) {
        excludedFacets.push(FacetTypes.LOCATION_ID);
    }

    return (
        <SearchPageContainer
            archivedSearch={archivedSearch}
            authToken={authToken}
            baseUrl={baseUrl}
            changePaginationPageSize={(pageSize: number | 'all') => dispatch(changePaginationPageSize(pageSize))}
            excludedFacets={excludedFacets}
            facets={facets}
            fetchSearchSuggestions={(term: string, secondary?: boolean) =>
                dispatch(fetchSearchSuggestions(term, secondary, false))
            }
            filterTerms={filterTerms}
            formatMessage={formatMessage}
            houses={houses}
            isLoading={isLoading}
            isLoggedIn={isLoggedIn}
            isSavedSearch={isSavedSearch}
            loginModalOpen={loginModalOpen}
            navigate={navigate}
            openLoginModal={() => dispatch(openLoginModal())}
            page={page}
            pageName={pageName}
            pageSize={pageSize}
            pathname={location.pathname}
            queryParams={queryParams}
            rawFacets={rawFacets}
            saveSearchParams={saveSearchParams}
            searchFailed={searchFailed}
            searchQuery={searchQuery}
            searchSubmitted={searchSubmitted}
            sellerId={sellerId}
            sellersInSearchResults={sellersInSearchResults}
            setHeaderDropdownVisible={(show?: boolean) => dispatch(setHeaderDropdownVisible(show))}
            sortOptions={sortOptions}
            submitSaveSearch={(ssParams: ParamsOrKey, sendAlert: boolean) =>
                dispatch(submitSaveSearch(ssParams, sendAlert))
            }
            submitSearch={(params: Parameters<typeof submitSearch>[0]) => dispatch(submitSearch(params))}
            suggestions={suggestions}
            totalFound={totalFound}
            totalLiveItems={totalLiveItems}
            totalPages={totalPages}
            totalSoldItems={totalSoldItems}
            userExcludedHouses={userExcludedHouses}
            whiteLabel={whiteLabel}
        />
    );
};

export default SearchPageContainerWrapper;
